// src/pages/LoginPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  24News Login Page — Full version
//  • Accepts username OR email address in the login field
//  • i18n (en / fr / ar / de)
//  • RTL support, dark mode, reduced motion, fully responsive
//  • Remember me (localStorage vs sessionStorage)
//  • Optimistic loading / success / error states
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";

import en from "../i18n/translations/en.json";
import ar from "../i18n/translations/ar.json";
import fr from "../i18n/translations/fr.json";
import de from "../i18n/translations/de.json";

const API_BASE         = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/api";
const STORAGE_LANG_KEY = "site_lang";

export default function LoginPage() {
  // ── i18n ──────────────────────────────────────────────────────────────────
  const [currentLang] = useState(
    () => localStorage.getItem(STORAGE_LANG_KEY) || "en"
  );

  const translations = useMemo(() => {
    const bundle = { en, ar, fr, de };
    const selected = bundle[currentLang] || en;
    return selected.default ? selected.default : selected;
  }, [currentLang]);

  const t = (key) => {
    if (!key) return "";
    if (
      Object.prototype.hasOwnProperty.call(translations, key) &&
      typeof translations[key] === "string"
    ) {
      return translations[key];
    }
    const parts = key.split(".");
    let cur = translations;
    for (const p of parts) {
      if (
        cur &&
        typeof cur === "object" &&
        Object.prototype.hasOwnProperty.call(cur, p)
      ) {
        cur = cur[p];
      } else {
        return key;
      }
    }
    return typeof cur === "string" ? cur : key;
  };

  const isRTL = currentLang === "ar";

  // ── State ──────────────────────────────────────────────────────────────────
  // The field is called "username" internally (Django simplejwt convention),
  // but the label / placeholder say "Username or Email".
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [status,       setStatus]       = useState("idle"); // idle | loading | success | error
  const [errorMsg,     setErrorMsg]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember,     setRemember]     = useState(true);
  const [fieldErrors,  setFieldErrors]  = useState({ username: "", password: "" });

  const navigate           = useNavigate();
  const [search]           = useSearchParams();
  const next               = search.get("next") || "/";
  const usernameRef        = useRef(null);
  const passwordRef        = useRef(null);
  const pendingNavigateRef = useRef(null);

  // ── Setup ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    axios.defaults.baseURL = API_BASE;
    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

  useEffect(() => {
    usernameRef.current?.focus();
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    localStorage.setItem(STORAGE_LANG_KEY, currentLang);
  }, [isRTL, currentLang]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateField = (name, value) => {
    if (name === "username") {
      if (!value.trim())
        return t("login_field_required") || "Username or email is required.";
      return "";
    }
    if (name === "password") {
      if (!value.trim())
        return t("password_required") || "Password is required.";
      if (value.length < 6)
        return t("password_too_short") || "Password must be at least 6 characters.";
      return "";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (status === "error") {
      setStatus("idle");
      setErrorMsg("");
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setFieldErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  const validateForm = () => {
    const uErr = validateField("username", formData.username);
    const pErr = validateField("password", formData.password);
    setFieldErrors({ username: uErr, password: pErr });
    return !uErr && !pErr;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setStatus("loading");
    setErrorMsg("");

    try {
      // POST /api/token/
      // Body: { username: "<username or email>", password: "..." }
      // The Django EmailOrUsernameBackend resolves to the correct user.
      const res = await axios.post("/token/", {
        username: formData.username.trim(),
        password: formData.password,
      });

      const { access, refresh } = res.data || {};
      const store = remember ? localStorage : sessionStorage;

      if (access) {
        store.setItem("accessToken", access);
        axios.defaults.headers.common["Authorization"] = `Bearer ${access}`;
      }
      if (refresh) {
        store.setItem("refreshToken", refresh);
      }

      setStatus("success");
      if (pendingNavigateRef.current) clearTimeout(pendingNavigateRef.current);
      pendingNavigateRef.current = setTimeout(() => {
        navigate(next, { replace: true });
      }, 1200);
    } catch (err) {
      const serverMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.non_field_errors?.[0] ||
        (err?.message && err.message !== "Network Error" ? err.message : null);

      const getErrorKey = (msg) => {
        if (!msg) return "login_failed";
        const m = msg.toLowerCase();
        if (m.includes("no active account"))    return "login_failed";
        if (m.includes("invalid credentials"))  return "login_failed";
        if (m.includes("inactive"))             return "account_inactive";
        if (m.includes("network"))              return "network_error";
        if (m.includes("token"))                return "session_expired";
        return "login_failed";
      };

      setStatus("error");
      setErrorMsg(getErrorKey(serverMsg));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={`login-page ${isRTL ? "rtl" : ""}`}>
      <Helmet>
  <meta name="robots" content="noindex, nofollow" />
</Helmet>
      <style>{`
        /* ── Reset & tokens ── */
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; }

        :root {
          --primary:        #0c3cff;
          --primary-dark:   #0a2dd6;
          --primary-light:  #3d5dff;
          --success:        #10b981;
          --error:          #ef4444;
          --warning:        #f59e0b;
          --bg-primary:     #fbfcff;
          --bg-secondary:   #f8fafc;
          --surface:        #ffffff;
          --text-primary:   #061226;
          --text-secondary: #6b7280;
          --border:         #e5e7eb;
          --border-light:   #f3f4f6;
          --radius-sm:      8px;
          --radius-md:      12px;
          --radius-lg:      16px;
          --shadow-sm:      0 2px 8px rgb(255, 255, 255);
          --shadow-md:      0 8px 24px rgba(255, 255, 255, 0.08);
          --shadow-lg:      0 20px 60px rgba(227, 228, 233, 0.12);
          --transition:     cubic-bezier(0.2,0.8,0.2,1);
        }

        @media (prefers-color-scheme: dark) {
          :root {
            --bg-primary:    #fdfdfd;
            --bg-secondary:  #f7f7f7;
            --surface:       #f4f5f8;
            --text-primary:  #020202;
            --text-secondary:#8891a8;
            --border:        rgba(255, 255, 255, 0.09);
            --border-light:  rgba(255, 255, 255, 0.04);
            --shadow-lg:     0 20px 60px rgba(10, 10, 10, 0.54);
          }
        }

        /* ── Page ── */
        .login-page {
          min-height: 100vh;
          background: linear-gradient(135deg, var(--bg-primary) 0%, #ffffff00 100%, var(--bg-secondary) 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          color: var(--text-primary);
          overflow-x: hidden;
        }

        .login-page.rtl { direction: rtl; }

        /* Floating orbs */
        .login-page::before,
        .login-page::after {
          content: "";
          position: fixed;
          border-radius: 50%;
          opacity: 0.03;
          pointer-events: none;
          z-index: 0;
        }
        .login-page::before {
          width: 500px; height: 500px;
          background: var(--primary);
          top: -200px; right: -200px;
          animation: float 25s ease-in-out infinite;
        }
        .login-page::after {
          width: 400px; height: 400px;
          background: var(--success);
          bottom: -150px; left: -150px;
          animation: float 30s ease-in-out infinite reverse;
        }

        /* ── Wrapper ── */
        .login-wrapper {
          width: 100%;
          max-width: 480px;
          position: relative;
          z-index: 1;
          animation: slideUp 0.8s var(--transition);
        }

        /* ── Card ── */
        .login-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          position: relative;
          overflow: hidden;
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
        }
        .login-card::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--primary), var(--primary-light));
          transition: background 0.4s ease;
        }
        .login-card.error   { border-color: var(--error);   animation: shake 0.5s ease; }
        .login-card.success { border-color: var(--success); }
        .login-card.error::before   { background: linear-gradient(90deg, var(--error),   #f87171); }
        .login-card.success::before { background: linear-gradient(90deg, var(--success), #34d399); }

        .login-content { padding: 48px 40px; }

        /* ── Header ── */
        .login-header {
          margin-bottom: 32px;
          animation: fadeInDown 0.6s var(--transition);
        }
        .login-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }
        .login-dot {
          width: 12px; height: 12px;
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }
        .login-header h1 {
          font-size: 32px;
          font-weight: 700;
          margin: 0 0 8px;
          line-height: 1.2;
          letter-spacing: -0.5px;
          color: var(--text-primary);
        }
        .login-header p {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0;
          font-weight: 500;
        }

        /* ── Form ── */
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
          animation: fadeInUp 0.6s var(--transition);
        }
        .form-group:nth-child(1) { animation-delay: 0.1s; }
        .form-group:nth-child(2) { animation-delay: 0.15s; }

        /* ── Label ── */
        .form-label {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-primary);
          opacity: 0.8;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .form-label-main { flex: 1; }

        .forgot-link {
          color: var(--primary);
          text-decoration: none;
          font-size: 12px;
          font-weight: 600;
          transition: all 0.2s ease;
          opacity: 0.9;
        }
        .forgot-link:hover { opacity: 1; text-decoration: underline; }

        /* ── Input ── */
        .form-input-wrapper {
         --border:        rgba(2, 2, 2, 0.09);
          position: relative;
          display: flex;
          align-items: center;
        }
        .form-input {
          width: 100%;
          padding: 13px 16px;
          background: var(--bg-secondary);
          border: 1.5px solid var(--border);
          border-radius: var(--radius-md);
          font-size: 15px;
          line-height: 1.5;
          font-family: inherit;
          color: var(--text-primary);
          transition: all 0.2s ease;
        }
        .form-input::placeholder { color: #b3b9c8; }
        .form-input:focus {
          outline: none;
          background: var(--surface);
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(12,60,255,0.1);
          transform: translateY(-1px);
        }
        .form-input.error {
          border-color: var(--error);
          background: rgba(239,68,68,0.02);
        }
        .form-input.error:focus {
          box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
        }

        /* ── Input hint (@ icon) ── */
        .form-input-hint {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 13px;
          color: var(--text-secondary);
          opacity: 0.45;
          pointer-events: none;
          user-select: none;
        }
        .login-page.rtl .form-input-hint { right: auto; left: 14px; }

        /* ── Password toggle ── */
        .password-toggle {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          color: var(--text-secondary);
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          padding: 6px 10px;
          border-radius: 6px;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          font-family: inherit;
        }
        .login-page.rtl .password-toggle { right: auto; left: 12px; }
        .password-toggle:hover  { background: var(--border-light); color: var(--text-primary); }
        .password-toggle:active { transform: scale(0.95); }

        /* ── Field error ── */
        .field-error {
          font-size: 12px;
          color: var(--error);
          margin-top: 2px;
          display: flex;
          align-items: center;
          gap: 6px;
          animation: slideDown 0.2s ease;
        }

        /* ── Form-level error ── */
        .form-error {
          padding: 12px 14px;
          background: rgba(239,68,68,0.06);
          border: 1px solid rgba(239,68,68,0.2);
          border-radius: var(--radius-sm);
          font-size: 13px;
          color: #b91c1c;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 10px;
          animation: shake 0.3s ease;
        }
        .form-error::before { content: "⚠"; font-size: 16px; flex-shrink: 0; }

        /* ── Checkbox ── */
        .form-checkbox-wrapper {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 4px;
        }
        .form-checkbox-input {
          position: absolute;
          width: 1px; height: 1px;
          padding: 0; margin: -1px;
          overflow: hidden;
          clip: rect(0,0,0,0);
          white-space: nowrap;
          border: 0;
        }
        .form-checkbox-visual {
          width: 18px; height: 18px; min-width: 18px;
          border: 2px solid var(--border);
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          background: var(--surface);
        }
        .form-checkbox-input:checked + .form-checkbox-visual {
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          border-color: transparent;
          transform: scale(1.05);
        }
        .form-checkbox-input:checked + .form-checkbox-visual::after {
          content: "✓";
          color: #fff;
          font-size: 12px;
          font-weight: 800;
        }
        .form-checkbox-input:focus + .form-checkbox-visual {
          box-shadow: 0 0 0 3px rgba(12,60,255,0.1);
        }
        .form-checkbox-label {
          font-size: 13px;
          color: var(--text-secondary);
          font-weight: 500;
          cursor: pointer;
          user-select: none;
        }

        /* ── Submit button ── */
        .submit-button {
          position: relative;
          overflow: hidden;
          width: 100%;
          padding: 13px 24px;
          margin-top: 12px;
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          color: #fff;
          border: none;
          border-radius: var(--radius-md);
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.03em;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(12,60,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          min-height: 46px;
          font-family: inherit;
        }
        .submit-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(12,60,255,0.3);
        }
        .submit-button:active:not(:disabled) { transform: translateY(0); }
        .submit-button:disabled { opacity: 0.7; cursor: not-allowed; }

        .submit-button.loading { background: linear-gradient(135deg, #4060ff, #6b5aff); }
        .submit-button.success { background: linear-gradient(135deg, var(--success), #059669); }
        .submit-button.error   {
          background: linear-gradient(135deg, var(--error), #dc2626);
          box-shadow: 0 4px 12px rgba(239,68,68,0.25);
        }
        .submit-button.error:hover:not(:disabled) {
          box-shadow: 0 8px 24px rgba(239,68,68,0.35);
        }

        .button-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
        }
        .button-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        .button-check { animation: scaleIn 0.4s var(--transition); }

        /* ── Footer ── */
        .login-footer {
          margin-top: 28px;
          text-align: center;
          padding-top: 24px;
          border-top: 1px solid var(--border-light);
          font-size: 14px;
          color: var(--text-secondary);
          animation: fadeInUp 0.6s var(--transition);
          animation-delay: 0.3s;
        }
        .register-link {
          color: var(--primary);
          text-decoration: none;
          font-weight: 700;
          margin-left: 4px;
          transition: all 0.2s ease;
        }
        .login-page.rtl .register-link { margin-left: 0; margin-right: 4px; }
        .register-link:hover { text-decoration: underline; color: var(--primary-dark); }

        .legal-notice {
          margin-top: 40px;
          font-size: 12px;
          color: #a0a8b8;
          text-align: center;
          font-weight: 500;
          opacity: 0.7;
          animation: fadeIn 0.6s var(--transition);
          animation-delay: 0.4s;
        }

        /* ── Animations ── */
        @keyframes slideUp    { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:none} }
        @keyframes fadeInDown { from{opacity:0;transform:translateY(-10px)} to{opacity:1;transform:none} }
        @keyframes fadeInUp   { from{opacity:0;transform:translateY(10px)}  to{opacity:1;transform:none} }
        @keyframes fadeIn     { from{opacity:0} to{opacity:1} }
        @keyframes shake      { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-8px)} 75%{transform:translateX(8px)} }
        @keyframes spin       { to{transform:rotate(360deg)} }
        @keyframes pulse      { 0%,100%{opacity:1} 50%{opacity:0.6} }
        @keyframes float      { 0%,100%{transform:translateY(0)} 50%{transform:translateY(30px)} }
        @keyframes slideDown  { from{opacity:0;transform:translateY(-5px)} to{opacity:1;transform:none} }
        @keyframes scaleIn    { from{opacity:0;transform:scale(0.8)} to{opacity:1;transform:scale(1)} }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation: none !important; transition: none !important; }
        }

        /* ── Responsive ── */
        @media (max-width: 480px) {
          .login-page    { padding: 16px; }
          .login-content { padding: 32px 24px; }
          .login-header h1   { font-size: 26px; }
          .login-header      { margin-bottom: 28px; }
          .login-form        { gap: 18px; }
          .submit-button     { padding: 12px 20px; }
          .login-card        { border-radius: var(--radius-md); box-shadow: 0 10px 30px rgba(10,18,40,0.1); }
          .login-page::before { width:300px;height:300px;top:-150px;right:-150px; }
          .login-page::after  { width:250px;height:250px;bottom:-100px;left:-100px; }
        }
        @media (max-width: 360px) {
          .login-content { padding: 24px 18px; }
          .login-header h1 { font-size: 24px; }
          .form-label  { font-size: 11px; }
          .form-input  { font-size: 14px; padding: 10px 14px; }
          .forgot-link { font-size: 11px; }
        }
      `}</style>

      <div className="login-wrapper">
        <div
          className={`login-card ${
            status === "error" ? "error" : status === "success" ? "success" : ""
          }`}
          role="main"
          aria-labelledby="login-heading"
        >
          <div className="login-content">

            {/* ── Brand ── */}
            <header className="login-header">
              <div className="login-brand">
                <div className="login-dot" aria-hidden />
                <span style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" }}>
                  24News
                </span>
              </div>
              <h1 id="login-heading">{t("sign_in")}</h1>
              <p>{t("welcome_newsroom")}</p>
            </header>

            <form onSubmit={handleLogin} className="login-form" noValidate>

              {/* ── Username OR Email field ── */}
              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  <span className="form-label-main">
                    {t("login_field_label") || "Username or Email"}
                  </span>
                </label>
                <div className="form-input-wrapper">
                  <input
                    id="username"
                    ref={usernameRef}
                    name="username"
                    type="text"
                    inputMode="email"
                    autoComplete="username"
                    required
                    placeholder={
                      t("login_field_placeholder") || "username or email@example.com"
                    }
                    value={formData.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={status === "loading" || status === "success"}
                    className={`form-input ${fieldErrors.username ? "error" : ""}`}
                    aria-label={t("login_field_label") || "Username or Email"}
                    aria-invalid={!!fieldErrors.username}
                    aria-describedby={
                      fieldErrors.username ? "username-error" : undefined
                    }
                  />
                  {/* Small @ hint icon to signal email is accepted */}
                  {!formData.username && (
                    <span className="form-input-hint" aria-hidden>@</span>
                  )}
                </div>
                {fieldErrors.username && (
                  <div className="field-error" id="username-error" role="alert">
                    <span aria-hidden>⚠</span> {fieldErrors.username}
                  </div>
                )}
              </div>

              {/* ── Password field ── */}
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  <span className="form-label-main">{t("password")}</span>
                  <Link to="/forgot" className="forgot-link">
                    {t("forgot_password")}
                  </Link>
                </label>
                <div className="form-input-wrapper">
                  <input
                    id="password"
                    ref={passwordRef}
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={status === "loading" || status === "success"}
                    className={`form-input ${fieldErrors.password ? "error" : ""}`}
                    aria-label={t("password")}
                    aria-invalid={!!fieldErrors.password}
                    aria-describedby={
                      fieldErrors.password ? "password-error" : undefined
                    }
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-pressed={showPassword}
                    aria-label={
                      showPassword
                        ? t("hide_password") || "Hide password"
                        : t("show_password") || "Show password"
                    }
                  >
                    {showPassword ? t("Hide") || "Hide" : t("Show") || "Show"}
                  </button>
                </div>
                {fieldErrors.password && (
                  <div className="field-error" id="password-error" role="alert">
                    <span aria-hidden>⚠</span> {fieldErrors.password}
                  </div>
                )}
              </div>

              {/* ── Server error ── */}
              {status === "error" && (
                <div className="form-error" role="alert" aria-live="assertive">
                  {t(errorMsg) || errorMsg}
                </div>
              )}

              {/* ── Remember me ── */}
              <div className="form-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="remember"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  disabled={status === "loading" || status === "success"}
                  className="form-checkbox-input"
                />
                <span className="form-checkbox-visual" aria-hidden />
                <label htmlFor="remember" className="form-checkbox-label">
                  {t("remember_me")}
                </label>
              </div>

              {/* ── Submit ── */}
              <button
                type="submit"
                className={`submit-button ${status}`}
                disabled={status === "loading" || status === "success"}
                aria-busy={status === "loading"}
              >
                <span className="button-content">
                  {status === "loading" && (
                    <div className="button-spinner" aria-hidden />
                  )}
                  {status === "success" && (
                    <span className="button-check" aria-hidden>✓</span>
                  )}
                  {(status === "idle" || status === "error") && t("sign_in")}
                  {status === "loading" && (t("signing_in") || "Signing in...")}
                  {status === "success" && (t("success") || "Success!")}
                </span>
              </button>
            </form>

            {/* ── Footer ── */}
            <footer className="login-footer">
              <span>{t("no_account")}</span>
              <Link to="/register" className="register-link">
                {t("register")}
              </Link>
            </footer>
          </div>
        </div>
      </div>

      <div className="legal-notice">
        © {new Date().getFullYear()} {t("news_platform") || "Newsroom"}.{" "}
        {t("all_rights_reserved")}
      </div>
    </div>
  );
}